import { useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { T } from "../../styles/GlobalStyles";
import { ICONS } from "../../constants/icons";
import Ic from "../common/Ic";
import GlowDot from "../common/GlowDot";
import BrandLogo from "../common/BrandLogo";
import { useDeviceContext } from "../../hooks/useDeviceContext";
import { Dumbbell } from "lucide-react";

const NAV_ITEMS = [
  { id: "dashboard", icon: ICONS.layoutDashboard, label: "Tableau de bord" },
  { id: "calendar", icon: ICONS.cal, label: "Calendrier" },
  { id: "workouts", icon: null, label: "Séances", isDumbbell: true },
  { id: "clients", icon: ICONS.clients, label: "Clients" },
  { id: "insights", icon: "✦", label: "Insights IA", isSparkle: true },
  { id: "billing", icon: ICONS.billing, label: "Finances" },
];

const Sidebar = ({
  view,
  setView,
  onNavItem,
  onLogoClick,
  onSettings,
  brandName,
  logoUrl,
  demoMode,
  onToggleDemo,
  onResetDemo,
  badges = {},
  onSignOut,
  adminHref,
}) => {
  const goNav = onNavItem || ((id) => setView(id));
  const navRef = useRef(null);
  const { isBureau } = useDeviceContext();

  const handleNavKeyDown = useCallback((e) => {
    if (!isBureau) return;
    const btns = navRef.current?.querySelectorAll('[role="link"]');
    if (!btns?.length) return;

    const idx = Array.from(btns).indexOf(document.activeElement);
    if (idx === -1) return;

    let next;
    if (e.key === "ArrowDown") next = (idx + 1) % btns.length;
    else if (e.key === "ArrowUp") next = (idx - 1 + btns.length) % btns.length;
    else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      document.activeElement.click();
      return;
    } else return;

    e.preventDefault();
    btns[next].focus();
  }, [isBureau]);

  return (
    <div style={{ width: 68, background: T.surface, borderRight: `1px solid ${T.border}`, display: "flex", flexDirection: "column", alignItems: "center", padding: "16px 0", gap: 8, flexShrink: 0 }}>
      <button
        onClick={() => (onLogoClick ? onLogoClick() : setView("dashboard"))}
        style={{ marginBottom: 12 }}
        aria-label="Accueil"
      >
        <BrandLogo brandName={brandName} logoUrl={logoUrl} size={44} />
      </button>
      <nav ref={navRef} role="navigation" aria-label="Navigation principale" onKeyDown={handleNavKeyDown} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
        {NAV_ITEMS.map((it) => {
          const active = view === it.id;
          return (
            <button
              key={it.id}
              role="link"
              onClick={() => goNav(it.id)}
              aria-label={it.label}
              aria-current={active ? "page" : undefined}
              tabIndex={0}
              style={{ width: 44, height: 44, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", background: "transparent", color: active ? T.accent : T.muted, transition: "color 200ms ease", border: "1px solid transparent", position: "relative" }}
            >
              {active && (
                <motion.div
                  layoutId="sidebar-active"
                  style={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: 12,
                    background: T.accentBg,
                    border: `1px solid ${T.accent}40`,
                  }}
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                />
              )}
              {it.isSparkle ? (
                <span style={{ fontSize: 16, position: "relative", zIndex: 1 }}>{it.icon}</span>
              ) : it.isDumbbell ? (
                <span style={{ position: "relative", zIndex: 1, display: "flex", color: active ? T.accent : T.muted }}>
                  <Dumbbell size={20} strokeWidth={2} style={{ transform: "scaleX(-1)" }} />
                </span>
              ) : (
                <Ic d={it.icon} size={18} style={{ position: "relative", zIndex: 1 }} />
              )}
              {(badges[it.id] || (it.id === "insights" && badges.insights !== false)) && (
                <div style={{ position: "absolute", top: 4, right: 4, zIndex: 2 }}>
                  <GlowDot size={6} />
                </div>
              )}
            </button>
          );
        })}
      </nav>
      <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 8, alignItems: "center" }}>
        <button
          onClick={onToggleDemo}
          aria-label={demoMode ? "Désactiver le mode démo" : "Activer le mode démo"}
          title={demoMode ? "Mode démo (données fictives)" : "Mode vide (empty states)"}
          style={{
            width: 44, height: 44, borderRadius: 12,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: demoMode ? `${T.accent}18` : T.surface3,
            border: `1px solid ${demoMode ? T.accent + "50" : T.border}`,
            color: demoMode ? T.accent : T.muted,
            transition: "all .2s", cursor: "pointer", position: "relative",
          }}
        >
          <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        </button>
        {demoMode && onResetDemo && (
          <button
            onClick={onResetDemo}
            aria-label="Réinitialiser les données démo"
            title="Reset données démo"
            style={{
              width: 44, height: 44, borderRadius: 12,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: T.surface3,
              border: `1px solid ${T.border}`,
              color: T.muted,
              transition: "all .2s", cursor: "pointer",
            }}
          >
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 2v6h-6" />
              <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
              <path d="M3 22v-6h6" />
              <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
            </svg>
          </button>
        )}
        {adminHref && (
          <Link
            to={adminHref}
            title="Backoffice admin"
            aria-label="Backoffice admin"
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: T.accent,
              background: `${T.accent}12`,
              border: `1px solid ${T.accent}35`,
              fontSize: 11,
              fontWeight: 800,
              textDecoration: "none",
            }}
          >
            A
          </Link>
        )}
        {onSignOut && (
          <button
            type="button"
            onClick={onSignOut}
            aria-label="Me déconnecter"
            title="Déconnexion"
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: T.muted,
              background: T.surface3,
              border: `1px solid ${T.border}`,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
            </svg>
          </button>
        )}
        <button onClick={onSettings} aria-label="Paramètres" style={{ width: 44, height: 44, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", color: view === "settings" ? T.accent : T.muted, background: view === "settings" ? T.accentBg : "transparent", border: view === "settings" ? `1px solid ${T.accent}40` : "1px solid transparent", transition: "all .2s" }}>
          <Ic d={ICONS.settings} size={18} />
        </button>
        <div style={{ width: 34, height: 34, borderRadius: "50%", background: T.surface3, border: `2px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }} aria-hidden="true">💪</div>
      </div>
    </div>
  );
};
export default Sidebar;
